/**
 * A price step's bases on screen (`./price-basis.ts`, `docs/adr/6024`): in «Perché» each recorded
 * basis is a row with its label and what it means; on a step's head only the flagged ones, as
 * chips. A basis the run did not record draws nothing — never a guessed one.
 */

import { useTranslation } from 'react-i18next';

import { formatPercent } from '@/lib/format';
import { Badge } from '@/primitives';

import { elasticityBasis, flaggedBases, originalPriceBasis, shrinkageOf, type BasisPresentation, type Shrinkage, type StepBases } from './price-basis';

function BasisBadge({ basis }: { readonly basis: BasisPresentation }): React.JSX.Element {
  return (
    <span data-basis={basis.code} data-basis-flagged={basis.flagged ? 'true' : 'false'}>
      <Badge labelKey={basis.labelKey} values={{ code: basis.code }} />
    </span>
  );
}

/**
 * An own elasticity shrunk toward a pool (platform ADR 6085 amendment, `docs/adr/6026`): one line
 * naming the pool and the article's own weight, in words, never a colour. The weight is formatted
 * here and handed to the catalogue as a placeholder.
 */
function ShrinkageLine({ shrinkage }: { readonly shrinkage: Shrinkage }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <span className="text-caption text-ink-primary" data-basis-shrunk-toward={shrinkage.toward}>
      {shrinkage.ownWeight === null
        ? t('inseason.priceBasis.shrunkTowardNoWeight', { pool: shrinkage.toward })
        : t('inseason.priceBasis.shrunkToward', { pool: shrinkage.toward, weight: formatPercent(shrinkage.ownWeight, i18n.language, 0) })}
    </span>
  );
}

function BasisRow({ titleKey, basis }: { readonly titleKey: string; readonly basis: BasisPresentation }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-caption text-ink-secondary">{t(titleKey)}</dt>
      <dd className="flex flex-col gap-1">
        <span><BasisBadge basis={basis} /></span>
        {basis.pool !== null && (
          <span className="text-caption text-ink-primary" data-basis-pool={basis.pool}>{t('inseason.priceBasis.pool', { pool: basis.pool })}</span>
        )}
        {basis.shrinkage !== null && <ShrinkageLine shrinkage={basis.shrinkage} />}
        <span className="text-caption text-ink-secondary max-w-prose">{t(basis.helpKey, { code: basis.code })}</span>
        {basis.shrinkage !== null && (
          <span className="text-caption text-ink-secondary max-w-prose">{t('inseason.help.priceBasis.shrunkToward')}</span>
        )}
      </dd>
    </div>
  );
}

/** «Perché»: each basis the run recorded, with what it means. Nothing when it recorded neither. */
export function PriceBasisRows({ bases }: { readonly bases: StepBases | null | undefined }): React.JSX.Element | null {
  const elasticity = elasticityBasis(bases?.elasticity_basis, bases?.elasticity_pool_key, shrinkageOf(bases));
  const original = originalPriceBasis(bases?.original_price_basis);
  if (elasticity === null && original === null) return null;
  return (
    <dl className="grid gap-3 sm:grid-cols-2" data-price-basis="">
      {elasticity !== null && <BasisRow titleKey="inseason.priceBasis.elasticityTitle" basis={elasticity} />}
      {original !== null && <BasisRow titleKey="inseason.priceBasis.originalPriceTitle" basis={original} />}
    </dl>
  );
}

/** A step's head or row: the bases that are not the usual one, as chips. Nothing otherwise. */
export function FlaggedBasisBadges({ step }: { readonly step: StepBases }): React.JSX.Element | null {
  const flagged = flaggedBases(step);
  if (flagged.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" data-price-basis-flags="">
      {flagged.map((basis) => <li key={basis.kind}><BasisBadge basis={basis} /></li>)}
    </ul>
  );
}
